import { useQuery } from "@tanstack/react-query";
import { Music, Plus, Search, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useSpotifyAuth } from "../hooks/useSpotifyAuth";
import { spotifyApi } from "../lib/spotify";
import { useLocalFilesStore } from "../stores/localFilesStore";
import { Track } from "../types";
import { ProviderTabs } from "./ProviderTabs";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";

interface AddTracksDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddTracks: (tracks: Track[]) => void;
  onRemoveTrack?: (trackId: string) => void;
  existingTracks?: Track[];
  title?: string;
}

export const AddTracksDialog: React.FC<AddTracksDialogProps> = ({
  isOpen,
  onClose,
  onAddTracks,
  onRemoveTrack,
  existingTracks = [],
  title = "Add Tracks",
}) => {
  const [activeProvider, setActiveProvider] = useState<"local" | "spotify">("local");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTracks, setSelectedTracks] = useState<Track[]>([]);

  const { files } = useLocalFilesStore();
  const { isAuthenticated } = useSpotifyAuth();

  const { data: spotifyResults, isLoading } = useQuery({
    queryKey: ["search", searchQuery, activeProvider],
    queryFn: () => spotifyApi.search(searchQuery, ["track"]),
    enabled: searchQuery.length > 0 && activeProvider === "spotify" && isAuthenticated,
  });

  const filteredLocalFiles = files.filter(
    (file) =>
      !existingTracks.some((existing) => existing.id === file.id) &&
      (searchQuery === "" ||
        file.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        file.artist.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const spotifyTracks =
    spotifyResults?.tracks.items.map((track) => ({
      id: track.id,
      title: track.name,
      artist: track.artists[0].name,
      albumArt: track.album.images[0]?.url,
      duration: track.duration_ms / 1000,
      url: track.preview_url || "",
      source: "spotify" as const,
      spotifyData: track,
    })) || [];

  const handleAddSelected = () => {
    onAddTracks(selectedTracks);
    setSelectedTracks([]);
  };

  const toggleTrackSelection = (track: Track) => {
    setSelectedTracks((prev) =>
      prev.some((t) => t.id === track.id) ? prev.filter((t) => t.id !== track.id) : [...prev, track]
    );
  };

  const TrackItem: React.FC<{ track: Track; isSelected?: boolean; onSelect?: () => void; onRemove?: () => void }> = ({
    track,
    isSelected,
    onSelect,
    onRemove,
  }) => (
    <div className="flex items-center gap-4 p-4 bg-zinc-800/50 rounded-lg group hover:bg-zinc-800">
      {track.albumArt ? (
        <img src={track.albumArt} alt={track.title} className="w-12 h-12 rounded-md object-cover" />
      ) : (
        <div className="w-12 h-12 bg-zinc-700 rounded-md flex items-center justify-center">
          <Music className="w-6 h-6 text-zinc-500" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <h4 className="font-medium truncate">{track.title}</h4>
        <p className="text-sm text-zinc-400 truncate">{track.artist}</p>
      </div>

      {onSelect && (
        <Button
          onClick={onSelect}
          variant={isSelected ? "default" : "outline"}
          size="icon"
          className={isSelected ? "bg-yellow-500 text-white hover:bg-yellow-600" : ""}
        >
          <Plus className="w-4 h-4" />
        </Button>
      )}

      {onRemove && (
        <Button
          onClick={onRemove}
          variant="ghost"
          size="icon"
          className="text-red-400 opacity-0 group-hover:opacity-100 transition"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          {/* Search and Provider Selection */}
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-zinc-400" />
              <Input
                type="text"
                placeholder="Search tracks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-3 bg-zinc-800 rounded-full"
              />
            </div>

            <ProviderTabs activeProvider={activeProvider} onProviderChange={setActiveProvider} />
          </div>

          {/* Available Tracks */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Available Tracks</h3>
              {selectedTracks.length > 0 && (
                <Button onClick={handleAddSelected} className="bg-yellow-500 text-white hover:bg-yellow-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Selected ({selectedTracks.length})
                </Button>
              )}
            </div>

            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {activeProvider === "local"
                ? filteredLocalFiles.map((track) => (
                    <TrackItem
                      key={track.id}
                      track={track}
                      isSelected={selectedTracks.some((t) => t.id === track.id)}
                      onSelect={() => toggleTrackSelection(track)}
                    />
                  ))
                : spotifyTracks.map((track) => (
                    <TrackItem
                      key={track.id}
                      track={track}
                      isSelected={selectedTracks.some((t) => t.id === track.id)}
                      onSelect={() => toggleTrackSelection(track)}
                    />
                  ))}
            </div>
          </div>

          {/* Existing Tracks */}
          {existingTracks.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Current Tracks</h3>
              <div className="max-h-[300px] overflow-y-auto space-y-2">
                {existingTracks.map((track) => (
                  <TrackItem
                    key={track.id}
                    track={track}
                    onRemove={onRemoveTrack ? () => onRemoveTrack(track.id) : undefined}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
